import React, { useEffect, useState } from 'react';
import { Save, CheckCircle, Eye, EyeOff, Cpu, Settings, Shield } from 'lucide-react';
import api from '../../utils/api';

export default function AdminConfig() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  useEffect(() => {
    api.get('/admin/config').then(r => setCfg(r.data)).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...cfg };
    // Only send the key if the user typed something new
    if (newApiKey.trim()) {
      payload.anthropic_api_key = newApiKey.trim();
    } else {
      delete payload.anthropic_api_key;
    }
    // Remove read-only fields
    delete payload.anthropic_api_key_configured;
    delete payload.anthropic_api_key_hint;
    await api.patch('/admin/config', payload);
    if (newApiKey.trim()) {
      setCfg(c => ({ ...c, anthropic_api_key_configured: true, anthropic_api_key_hint: newApiKey.slice(0, 8) + '••••••••' + newApiKey.slice(-4) }));
      setNewApiKey('');
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (loading || !cfg) return <div className="text-stone-400 text-sm">Carregando...</div>;

  return (
    <div>
      <h1 className="text-xl font-bold text-stone-900 mb-6">Configurações do Sistema</h1>

      <form onSubmit={save} className="max-w-xl space-y-6">

        {/* IA */}
        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2 border-b border-stone-100 pb-3">
            <Cpu size={16} className="text-[#0f3d2e]" />
            <h2 className="font-semibold text-stone-800 text-sm">Integrações de IA</h2>
          </div>

          <div>
            <label className="text-xs font-medium text-stone-600 block mb-1">
              Chave da API — Anthropic (Claude)
            </label>
            <div className="flex items-center gap-2 mb-2">
              {cfg.anthropic_api_key_configured ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold">
                  <CheckCircle size={11} /> Configurada
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold">
                  Não configurada
                </span>
              )}
              {cfg.anthropic_api_key_hint && (
                <span className="text-xs text-stone-400 font-mono">{cfg.anthropic_api_key_hint}</span>
              )}
            </div>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={newApiKey}
                onChange={e => setNewApiKey(e.target.value)}
                placeholder={cfg.anthropic_api_key_configured ? 'Digite nova chave para substituir...' : 'sk-ant-api03-...'}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:border-[#0f3d2e]"
              />
              <button
                type="button"
                onClick={() => setShowKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
              >
                {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            <p className="text-xs text-stone-400 mt-1.5">
              Usada pelo endpoint <span className="font-mono">/calculadora/tese</span> para gerar a análise de investimento com IA. Obtenha em{' '}
              <span className="font-mono text-stone-500">console.anthropic.com</span>.
            </p>
          </div>
        </div>

        {/* Sessão */}
        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2 border-b border-stone-100 pb-3">
            <Shield size={16} className="text-[#0f3d2e]" />
            <h2 className="font-semibold text-stone-800 text-sm">Parâmetros de Sessão</h2>
          </div>
          {[
            ['Expiração JWT (horas)', 'jwt_expiry_hours', 'number'],
            ['Validade de convite (horas)', 'invite_expiry_hours', 'number'],
            ['Tentativas máx. de login', 'max_login_attempts', 'number'],
          ].map(([label, key, type]) => (
            <div key={key}>
              <label className="text-xs font-medium text-stone-600 block mb-1">{label}</label>
              <input
                type={type}
                value={cfg[key] ?? ''}
                onChange={e => setCfg(c => ({ ...c, [key]: Number(e.target.value) }))}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e]"
              />
            </div>
          ))}
        </div>

        {/* Manutenção */}
        <div className="bg-white border border-stone-200 rounded-xl p-6 space-y-5">
          <div className="flex items-center gap-2 border-b border-stone-100 pb-3">
            <Settings size={16} className="text-[#0f3d2e]" />
            <h2 className="font-semibold text-stone-800 text-sm">Modo Manutenção</h2>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <div
              className={`w-10 h-6 rounded-full transition-colors relative ${cfg.maintenance_mode ? 'bg-red-500' : 'bg-stone-200'}`}
              onClick={() => setCfg(c => ({ ...c, maintenance_mode: !c.maintenance_mode }))}
            >
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${cfg.maintenance_mode ? 'translate-x-5' : 'translate-x-1'}`} />
            </div>
            <span className="text-sm font-medium text-stone-700">Plataforma em manutenção</span>
          </label>
          {cfg.maintenance_mode && (
            <div>
              <label className="text-xs font-medium text-stone-600 block mb-1">Mensagem customizável</label>
              <textarea
                value={cfg.maintenance_message || ''}
                onChange={e => setCfg(c => ({ ...c, maintenance_message: e.target.value }))}
                rows={3}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0f3d2e] resize-none"
                placeholder="Sistema em manutenção. Voltamos em breve."
              />
            </div>
          )}
        </div>

        <button
          type="submit"
          className={`flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-medium transition-colors ${saved ? 'bg-green-600 text-white' : 'bg-[#0f3d2e] text-white hover:bg-[#0a2e22]'}`}
        >
          {saved ? <><CheckCircle size={16} /> Salvo!</> : <><Save size={16} /> Salvar configurações</>}
        </button>
      </form>
    </div>
  );
}
